/* eslint-disable */
class ProductMeasurements extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.getAttribute('data-section-id');
    this.rangeErrorTemplate = this.getAttribute('data-range-error-template') || 'Please enter a number between [min] and [max] cm';
    this.numberErrorTemplate = this.getAttribute('data-number-error-template') || 'Please enter a number in cm';

    this.form = this.closest('form');
    this.customGroup = this.querySelector('[data-measurements-group="custom"]');
    this.standardGroup = this.querySelector('[data-measurements-group="standard"]');

    const mapElement = this.querySelector('[data-custom-variant-map]');
    this.customVariantMap = {};
    if (mapElement) {
      try {
        this.customVariantMap = JSON.parse(mapElement.textContent);
      } catch (error) {
        console.error('ProductMeasurements: could not parse variant map', error);
      }
    }

    this.onVariantUpdatedBound = this.onVariantUpdated.bind(this);
    this.onSubmitCaptureBound = this.onSubmitCapture.bind(this);
  }

  connectedCallback() {
    if (!this.form) return;
    this.applyMode(this.isCustomVariant(this.currentVariantId()));
    window.eventBus?.on('variant:updated', this.onVariantUpdatedBound);
    document.addEventListener('submit', this.onSubmitCaptureBound, true);

    this.querySelectorAll('[data-measure]').forEach((input) => {
      input.addEventListener('input', () => this.clearError(input));
      input.addEventListener('blur', () => {
        input.value = input.value.replace(',', '.').trim();
        if (input.value) this.validateInput(input);
      });
    });
  }

  disconnectedCallback() {
    window.eventBus?.off('variant:updated', this.onVariantUpdatedBound);
    document.removeEventListener('submit', this.onSubmitCaptureBound, true);
  }

  currentVariantId() {
    const idInput = this.form.querySelector('input[name="id"]');
    return idInput ? idInput.value : null;
  }

  isCustomVariant(variantId) {
    return !!(variantId && this.customVariantMap[variantId]);
  }

  onVariantUpdated() {
    /* ===== The variant id input inside the form is already replaced when this fires ===== */
    this.applyMode(this.isCustomVariant(this.currentVariantId()));
  }

  applyMode(isCustom) {
    /* ===== Toggle groups; values are intentionally never cleared ===== */
    if (this.customGroup) {
      this.customGroup.hidden = !isCustom;
      this.customGroup.querySelectorAll('[data-measure]').forEach((input) => {
        input.disabled = !isCustom;
        // Height is the only optional custom field — it has no data-error-message
        input.required = isCustom && input.hasAttribute('data-error-message');
      });
    }

    if (this.standardGroup) {
      const showStandard = !isCustom || !this.customGroup;
      this.standardGroup.hidden = !showStandard;
      this.standardGroup.querySelectorAll('[data-measure]').forEach((input) => {
        input.disabled = !showStandard;
      });
    }
  }

  onSubmitCapture(event) {
    if (event.target !== this.form) return;

    this.syncStandardMarker();

    let firstInvalid = null;
    this.querySelectorAll('[data-measure]:not(:disabled)').forEach((input) => {
      input.value = input.value.replace(',', '.').trim();
      // Empty inputs are left to the theme's own required-field validation
      if (!input.value) return;
      if (!this.validateInput(input) && !firstInvalid) firstInvalid = input;
    });

    if (firstInvalid) {
      event.preventDefault();
      event.stopPropagation();
      firstInvalid.focus();
    }
  }

  syncStandardMarker() {
    /* ===== Only mark the line when the customer actually left a measurement ===== */
    const marker = this.standardGroup?.querySelector('[data-marker="standard"]');
    if (!marker) return;
    const filled = Array.from(this.standardGroup.querySelectorAll('[data-measure]'))
      .some((input) => !input.disabled && input.value.trim());
    marker.disabled = !filled;
  }

  validateInput(input) {
    const min = parseFloat(input.getAttribute('data-min'));
    const max = parseFloat(input.getAttribute('data-max'));
    const value = input.value;

    let message = '';
    if (!/^\d{1,3}(\.\d)?$/.test(value)) {
      message = this.numberErrorTemplate;
    } else {
      const number = parseFloat(value);
      if (number < min || number > max) {
        message = this.rangeErrorTemplate.replace('[min]', min).replace('[max]', max);
      }
    }

    message ? this.showError(input, message) : this.clearError(input);
    return !message;
  }

  showError(input, message) {
    input.setAttribute('aria-invalid', 'true');
    const container = input.closest('.input-wrapper')?.querySelector('.input-error-container');
    if (container) {
      container.innerHTML = `<span class="required-field-error errors" style="display: block;">${message}</span>`;
    }
  }

  clearError(input) {
    input.removeAttribute('aria-invalid');
    const container = input.closest('.input-wrapper')?.querySelector('.input-error-container');
    if (container) container.innerHTML = '';
  }
}

if (!customElements.get('product-measurements')) {
  customElements.define('product-measurements', ProductMeasurements);
}
